import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { ResultadoBinario } from '@/types/enums';

import type { PoolLocal, PosicionHpgValida } from './hpgDialogTypes';

interface HpgDialogPoolCardProps {
  pool: PoolLocal;
  poolIndex: number;
  posicionesHpgValidas: PosicionHpgValida[];
  allUsedInOtherPools: Set<number>;
  onChange: (updated: Partial<PoolLocal>) => void;
  onDelete: () => void;
  canDelete: boolean;
  showErrors: boolean;
  disabled?: boolean;
}

export function HpgDialogPoolCard({
  pool,
  poolIndex,
  posicionesHpgValidas,
  allUsedInOtherPools,
  onChange,
  onDelete,
  canDelete,
  showErrors,
  disabled = false,
}: HpgDialogPoolCardProps): JSX.Element {
  const availableOptions = posicionesHpgValidas.filter(
    (p) => !allUsedInOtherPools.has(p.posicion) || pool.numeros_muestras.includes(p.posicion),
  );

  const selectedOptions = posicionesHpgValidas.filter((p) =>
    pool.numeros_muestras.includes(p.posicion),
  );

  const promedioHpg = useMemo(() => {
    const valores = pool.numeros_muestras
      .map((n) => posicionesHpgValidas.find((p) => p.posicion === n)?.valor)
      .filter((v): v is number => v !== null && v !== undefined);
    if (valores.length === 0) return null;
    return Math.round(valores.reduce((a, b) => a + b, 0) / valores.length);
  }, [pool.numeros_muestras, posicionesHpgValidas]);

  const noMuestras = pool.numeros_muestras.length === 0;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderColor: showErrors && noMuestras ? 'warning.main' : undefined }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Pool {poolIndex + 1}
        </Typography>
        <Tooltip title={!canDelete ? 'Debe haber al menos un pool.' : ''} placement="top">
          <span>
            <IconButton size="small" onClick={onDelete} disabled={!canDelete || disabled}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <Autocomplete
          multiple
          size="small"
          sx={{ flex: 1 }}
          options={availableOptions}
          getOptionLabel={(opt) =>
            `Muestra ${opt.posicion} (${opt.valor !== null ? `${opt.valor} hpg` : '—'})`
          }
          isOptionEqualToValue={(opt, val) => opt.posicion === val.posicion}
          value={selectedOptions}
          onChange={(_, newValue) => {
            onChange({ numeros_muestras: newValue.map((p) => p.posicion) });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Muestras HPG vinculadas"
              error={showErrors && noMuestras}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((opt, index) => (
              <Chip
                {...getTagProps({ index })}
                key={opt.posicion}
                label={`Muestra ${opt.posicion}`}
                size="small"
              />
            ))
          }
          noOptionsText="No hay muestras HPG disponibles"
          disabled={disabled}
        />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {promedioHpg !== null ? `Promedio: ${promedioHpg} hpg` : '—'}
        </Typography>
      </Box>

      {showErrors && noMuestras && (
        <FormHelperText sx={{ color: 'warning.main', mb: 1 }}>
          Agregá al menos una muestra.
        </FormHelperText>
      )}

      <Box sx={{ mb: 1.5 }}>
        <RadioGroup
          row
          value={pool.valor ?? ''}
          onChange={(e) => onChange({ valor: e.target.value as ResultadoBinario })}
        >
          <FormControlLabel
            value={ResultadoBinario.NEGATIVO}
            control={<Radio size="small" disabled={disabled} />}
            label="Negativo"
          />
          <FormControlLabel
            value={ResultadoBinario.POSITIVO}
            control={<Radio size="small" disabled={disabled} />}
            label="Positivo"
          />
        </RadioGroup>
        {showErrors && !pool.valor && (
          <FormHelperText error>Seleccioná un resultado</FormHelperText>
        )}
      </Box>

      <TextField
        label="Observación"
        size="small"
        fullWidth
        multiline
        rows={2}
        value={pool.observacion ?? ''}
        onChange={(e) => onChange({ observacion: e.target.value !== '' ? e.target.value : null })}
        disabled={disabled}
      />
    </Paper>
  );
}
